import { StepperModal } from "@etframework/ui.components";
import ReactDOM from "react-dom";
import useModalEvent from "../../hooks/useModalEvent";
import { useRef, useState, useEffect } from "react";
import { useWindowSize } from "../../hooks/useWindowSize";
import { CreateBoardForm } from "../../components/CreateBoardModalComponents/CreateBoardForm";
import { CreateTaskTypeForm } from "../../components/CreateBoardModalComponents/CreateTaskTypeForm";
import { AddBoardPermissionsForm } from "../../components/CreateBoardModalComponents/AddBoardPermissionsForm";
import { useNavigate } from "react-router-dom";
import {
  useCreateBoard,
  useUpdateBoardPermissions,
} from "../../data/mutations/boardMutations";
import { useCreateTaskType } from "../../data/mutations/taskTypeMutations";
import { useUsers } from "../../data/queries/userQueries";
import { useGroups } from "../../data/queries/groupQueries";
import { ValidationProvider, useValidation } from "@etframework/ui.components";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Enums from "../../types/enums";

export const CreateBoardModal = () => {
  return (
    <ValidationProvider>
      <CreateBoardModalContent />
    </ValidationProvider>
  );
};

export const CreateBoardModalContent = () => {
  const { t, i18n } = useTranslation(["CreateBoardModal", "Shared"]);
  const navigate = useNavigate();
  const { setValidationErrors, clearValidationErrors } = useValidation();

  const emptyFormData = {
    board: { Name: "", Description: "", IncludeTaxReturn: false },
    taskTypes: [{ key: "", title: "" }],
    boardPermissions: [],
  };

  //State
  const [windowHeight] = useWindowSize();
  const [step, setStep] = useState(0); // For display purposes
  const [disabled, setDisabled] = useState(false); // For display purposes
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(emptyFormData);

  // references
  const createBoardFormRef = useRef(null);
  const createTaskTypeFormRef = useRef(null);
  const addBoardPermissionsFormRef = useRef(null);
  const stepperModalRef = useRef(null);

  /**** Data and mutations ***/
  const { mutate: createBoard } = useCreateBoard();
  const { data: users } =
    useUsers(); /* It's up here so it doesn't keep refetching */
  const { data: groups } = useGroups();
  const { mutate: updateBoardPermissions } = useUpdateBoardPermissions();
  const { mutate: createTaskType } = useCreateTaskType();

  // **** Data Handlers and stuff ****
  const onSubmit = (goToStep) => {
    setFormData({
      ...formData,
      boardPermissions: addBoardPermissionsFormRef.current?.getValue(),
    });
    setDisabled(true);
    createBoard(
      { board: formData.board },
      {
        onSuccess: onPostBoardSuccess,
        onError: ({ data }) => onPostNewBoardFailure(goToStep, data),
      }
    );
  };

  const onPostBoardSuccess = async (response) => {
    /* 
        this weirdness is because Task types need to be posted secuentially, 
        or the isUnique validation will not work 
        
        Also, modal closing and redirecting should always happen regardless of errors
        creating task types or user permissions
      */
    const createTaskTypes = () =>
      formData.taskTypes.reduce(
        (promise, tt) =>
          promise.then(() => postTaskType(response, tt, formData)),
        Promise.resolve()
      );

    Promise.all([createTaskTypes(), putBoardPermissions(response)]).then(() => {
      stepperModalRef.current?.modalRef.current?.close();
      navigate(`/duedates/settings/boards/${response.Id}`);
    });
  };

  const onPostNewBoardFailure = (goToStep, data) => {
    goToStep(0);
    setStep(0);
    setDisabled(false);
    setValidationErrors(data);
  };

  const postTaskType = (createBoardResponse, tt, formData) => {
    return new Promise((resolve) =>
      createTaskType(
        {
          taskType: {
            ...tt,
            includeTaxReturn: formData?.board.IncludeTaxReturn,
            boardId: createBoardResponse.Id,
          },
        },
        {
          onSettled: () => resolve(),
          onError: (error, payload) => {
            toast.error(t("TaskTypeError", { name: payload.taskType.title }));
          },
        }
      )
    );
  };

  const putBoardPermissions = async (createBoardResponse) => {
    const boardId = createBoardResponse?.Id;
    const newBoardPermissions = addBoardPermissionsFormRef.current?.getValue();
    if (newBoardPermissions) {
      const currentBoardPermissions =
        createBoardResponse.Entity.BoardPermissions.map((bp) => ({
          PermissionType: bp.GroupId
            ? Enums.PermissionType.Group
            : Enums.PermissionType.User,
          Id: bp.GroupId ?? bp.UserId,
        }));

      updateBoardPermissions(
        {
          permissions: {
            boardId: boardId,
            entities: newBoardPermissions.concat(currentBoardPermissions),
          },
        },
        {
          onError: () => {
            toast.error(t("BoardPermissionsError"));
          },
        }
      );
    }
  };

  // Modal events
  const { ModalEvents, useRegisterEvent } = useModalEvent();

  const toggleModal = () => {
    stepperModalRef.current?.modalRef.current?.open();
  };

  const onModalClose = () => {
    clearValidationErrors();
    stepperModalRef.current.goToStep.current?.(0);
    setStep(0);
    setFormData(emptyFormData);
    setDisabled(false);
    setShowModal(false);
  };

  const onModalOpen = () => {
    setShowModal(true);
  };

  useRegisterEvent(ModalEvents.CreateBoardModal, toggleModal);

  // Stepper
  const onNextStep = ({ activeStep }) => {
    if (activeStep === 0) {
      const values = createBoardFormRef.current?.getValue();
      if (createBoardFormRef.current?.validate()) {
        setFormData({ ...formData, board: values });
        setStep((prev) => prev + 1);
        return true;
      } else {
        return false; // Prevent next step
      }
    } else if (activeStep === 1) {
      const values = createTaskTypeFormRef.current?.getValue();
      if (createTaskTypeFormRef.current?.validate()) {
        setFormData({
          ...formData,
          taskTypes: values,
          board: {
            ...formData?.board,
            IncludeTaxReturn:
              createTaskTypeFormRef.current?.getIncludeTaxReturnRef(),
          },
        });
        setStep((prev) => prev + 1);
        return true;
      } else {
        return false;
      }
    }
  };

  const onPreviousStep = (args) => {
    if (args.activeStep === 2)
      setFormData({
        ...formData,
        boardPermissions: addBoardPermissionsFormRef.current?.getValue(),
      });
    else if (args.activeStep === 1) {
      setFormData({
        ...formData,
        taskTypes: createTaskTypeFormRef.current?.getValue(),
        IncludeTaxReturn:
          createTaskTypeFormRef.current?.getIncludeTaxReturnRef(),
      });
    }
    setStep((prev) => prev - 1);
  };

  useEffect(() => {
    const buttonsRef = stepperModalRef.current.stepperRef().getButtonsRef();
    buttonsRef.setCreateText(t("Create"));
    buttonsRef.setBackText(t("Cancel"));
    buttonsRef.setContinueText(t("Continue"));
  }, [i18n.language]);

  return ReactDOM.createPortal(
    <>
      <StepperModal
        id={ModalEvents.CreateBoardModal.Id}
        ref={stepperModalRef}
        isModal
        showCloseIcon={true}
        visible={showModal}
        onNextStep={onNextStep}
        backText={t("Back")}
        continueText={t("Continue")}
        createText={t("Submit")}
        onPreviousStep={onPreviousStep}
        onSubmit={onSubmit}
        close={onModalClose}
        open={onModalOpen}
        minHeight={"20vh"}
        height={
          step === 2
            ? windowHeight > 1176
              ? "1176"
              : `${windowHeight}`
            : "800"
        }
        width="800px"
        steps={[
          {
            id: "create-board-step",
            component: (
              <CreateBoardForm
                id={ModalEvents.CreateBoardModal.Id + "_step1"}
                initState={formData.board}
                ref={createBoardFormRef}
              />
            ),
          },
          {
            id: "create-task-type-step",
            component: (
              <CreateTaskTypeForm
                id={ModalEvents.CreateBoardModal.Id + "_step2"}
                initState={formData.taskTypes}
                boardInitState={formData.board}
                ref={createTaskTypeFormRef}
              />
            ),
          },
          {
            id: "create-user-permissions",
            disabled: disabled,
            component: (
              <AddBoardPermissionsForm
                id={ModalEvents.CreateBoardModal.Id + "_step3"}
                boardName={formData.board.Name}
                initState={formData.boardPermissions}
                ref={addBoardPermissionsFormRef}
                users={users}
                groups={groups}
              />
            ),
          },
        ]}
      />
    </>,
    document.body
  );
};
